import { Fragment, useEffect, useState, ReactNode } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { State } from 'src/types';
import Head, { Seo } from 'src/utils/head';
import { throttle } from 'lodash';
import i18n from 'src/utils/translate';

import { resetFloatingSignupBox, fetchGuidelinesOnce } from 'src/modules/shared/context/actions';

import Alert from 'src/typescript/components/alert/alert';
import Animate from 'src/components/animate';
import CommunityHeader from 'src/modules/community/shared/components/header/communityHeader';
import ESocialPrescriptionCta from 'src/components/ESocialPrescriptionCta';
import FloatingSignupBox from 'src/components/floatingSignupBox';
import Footer from 'src/components/footer';
import Loader from 'src/components/loader';
import Modal from 'src/modules/shared/modal/components/modalRoot';
import SiteBar from 'src/components/siteBar/siteBar';
import VerificationBar from 'src/components/verificationBar';

type CommunityLayout = {
  children?: ReactNode;
  containerClassName?: string;
  currentPage?: string;
  seo?: Seo;
  sideBarContent?: ReactNode;
};

export default function CommunityLayout({
  children,
  containerClassName,
  currentPage,
  seo,
  sideBarContent,
}: CommunityLayout) {
  const dispatch = useDispatch();

  const { currentUser, currentCommunity, showVerificationBar, isFloatingSignupBoxVisible } = useSelector(({context}: State) => context);

  // Fetch community guidelines
  useEffect(() => {
    dispatch(fetchGuidelinesOnce(currentCommunity?.slug, currentCommunity?.isPrivate));
  }, [currentCommunity?.slug, currentCommunity?.isPrivate]);

  // set up scroll listener
  const [showScroll, setShowScroll] = useState(false);
  const [showFloatingSignupBox, setShowFloatingSignupBox] = useState(false);
  useEffect(() => {
    if (!isFloatingSignupBoxVisible) {
      dispatch(resetFloatingSignupBox());
    }
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const onScroll = throttle(() => {
    if (window.scrollY > 0) {
      setShowScroll(true);
      setShowFloatingSignupBox(true);
    } else {
      setShowScroll(false);
    }
  }, 300);

  return (
    <Fragment>
      {currentCommunity && <Head {...seo}/>}
      <SiteBar />
      {currentUser && showVerificationBar && <VerificationBar />}
      <ESocialPrescriptionCta />
      <Alert />
      {!currentCommunity
        ? <Loader />
        : (
          <main className="content-top-margin" role="main">
            <h1 className="sr-only">{seo?.title || 'HealthUnlocked | The social network for health'}</h1>
            <CommunityHeader currentPage={currentPage} />
            <div className={`results community-page-container ${containerClassName ? containerClassName : ''}`}>
              <div className={containerClassName === 'full-width' ? '' : 'results__content'}>
                {children}
              </div>
              {containerClassName !== 'full-width' &&
                <div className="results__sidebar">
                  {sideBarContent}
                </div>
              }
            </div>
          </main>
        )
      }
      <Animate classNames="TRANSITION-simpleFade" show={showScroll}>
        <button
          className={'back-to-top back-to-top--navbar icon-up-open'}
          title={i18n.t('Back to top')}
          type="button"
          onClick={() => window.scrollTo(0, 0)}
        />
      </Animate>
      <Footer />
      { // StickyFeaturedPost overwrites FloatingSignupBox for visitors on the single post page
        !currentUser && isFloatingSignupBoxVisible &&
          <FloatingSignupBox communitySlug={currentCommunity?.slug} show={showFloatingSignupBox} />
      }
      <Modal />
    </Fragment>
  );
}
